import { inject } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import {
  WebSocketConnection,
  WebSocketConnectionService,
  SubscriptionFactory,
} from "./websocket-connection/websocket-connection.service";
import { WEBSOCKET_ACTION_TYPE } from "../../constants/websocketActionTypes";

export interface RealtimeSubscriptionRequest {
  action?: WEBSOCKET_ACTION_TYPE;
  [key: string]: unknown;
}

/**
 * A caller-owned logical subscription. Call `replace` when its domain IDs
 * change and `close` when the caller is destroyed or no longer needs updates.
 */
export interface RealtimeSubscriptionHandle<TIncoming, TDescriptor> {
  readonly messages$: Observable<TIncoming>;
  replace(descriptor: TDescriptor): void;
  close(): void;
}

interface ActiveSubscription<TDescriptor> {
  descriptor: TDescriptor;
  references: number;
}

interface SubscriptionState<TDescriptor> {
  descriptor: TDescriptor;
  closed: boolean;
}

/**
 * Domain-level subscription service for an endpoint. Concrete services provide
 * protocol-specific descriptors; this class reference-counts them, shares one
 * connection, and supplies the current set whenever it reconnects after `4401`.
 *
 * Feature code should retain its returned subscription handle rather than manage
 * subscribe/unsubscribe messages directly.
 */
export abstract class RealtimeSubscriptionService<
  TIncoming,
  TDescriptor extends RealtimeSubscriptionRequest = RealtimeSubscriptionRequest,
> {
  protected abstract readonly PATH: string;
  protected abstract readonly VERSION: number;
  protected readonly TERMINAL_ERROR_CLOSE_CODES: readonly number[] = [];

  protected connectionService = inject(WebSocketConnectionService);

  private connection?: WebSocketConnection<TIncoming, TDescriptor>;
  private connectionSubscription?: Subscription;
  private readonly activeSubscriptions = new Map<string, ActiveSubscription<TDescriptor>>();
  private readonly subscriptionStates = new Map<number, SubscriptionState<TDescriptor>>();
  private nextSubscriptionId = 0;

  /**
   * Starts a logical subscription and returns its lifecycle handle. Identical
   * descriptors share one wire subscription until the last handle closes.
   */
  public register(descriptor: TDescriptor): RealtimeSubscriptionHandle<TIncoming, TDescriptor> {
    const subscriptionId = ++this.nextSubscriptionId;
    this.subscriptionStates.set(subscriptionId, { descriptor, closed: false });
    const addedDescriptor = this.addDescriptor(descriptor);
    const hadConnection = !!this.connection;
    const connection = this.ensureConnection();

    if (addedDescriptor && hadConnection && connection.isOpen) {
      connection.send(this.toSubscribeMessage(addedDescriptor));
    }

    return {
      messages$: connection.messages$,
      replace: (nextDescriptor) => this.replaceSubscription(subscriptionId, nextDescriptor),
      close: () => this.closeSubscription(subscriptionId),
    };
  }

  /**
   * Supports existing one-off protocol messages. Logical subscribe state must
   * be managed through subscription handles so it can be replayed after recovery.
   */
  public send(message: TDescriptor): void {
    this.connection?.send(message);
  }

  /**
   * Closes all subscription handles for this domain. Prefer calling `subscription.close()`
   * in feature code so concurrent consumers remain active.
   */
  public close(): void {
    this.subscriptionStates.clear();
    this.activeSubscriptions.clear();
    this.closeConnection();
  }

  private ensureConnection(): WebSocketConnection<TIncoming, TDescriptor> {
    if (!this.connection) {
      const subscriptionFactory: SubscriptionFactory<TDescriptor> = () => this.currentSubscribeMessages();
      const connection = this.connectionService.open<TIncoming, TDescriptor>(
        this.PATH,
        this.VERSION,
        subscriptionFactory,
        this.TERMINAL_ERROR_CLOSE_CODES
      );
      this.connection = connection;
      this.connectionSubscription = connection.messages$.subscribe({
        error: () => this.handleTerminalConnection(connection),
        complete: () => this.handleTerminalConnection(connection),
      });

      return connection;
    }

    return this.connection;
  }

  private replaceSubscription(subscriptionId: number, nextDescriptor: TDescriptor): void {
    const subscription = this.subscriptionStates.get(subscriptionId);
    if (!subscription || subscription.closed) {
      return;
    }

    const previousDescriptor = subscription.descriptor;
    if (this.subscriptionKey(previousDescriptor) === this.subscriptionKey(nextDescriptor)) {
      subscription.descriptor = nextDescriptor;
      return;
    }

    const removedDescriptor = this.removeDescriptor(previousDescriptor);
    const addedDescriptor = this.addDescriptor(nextDescriptor);
    subscription.descriptor = nextDescriptor;

    if (this.connection?.isOpen) {
      if (removedDescriptor) {
        this.connection.send(this.toUnsubscribeMessage(removedDescriptor));
      }
      if (addedDescriptor) {
        this.connection.send(this.toSubscribeMessage(addedDescriptor));
      }
    }
  }

  private closeSubscription(subscriptionId: number): void {
    const subscription = this.subscriptionStates.get(subscriptionId);
    if (!subscription || subscription.closed) {
      return;
    }

    subscription.closed = true;
    this.subscriptionStates.delete(subscriptionId);
    const removedDescriptor = this.removeDescriptor(subscription.descriptor);

    if (removedDescriptor && this.connection?.isOpen) {
      this.connection.send(this.toUnsubscribeMessage(removedDescriptor));
    }

    if (this.subscriptionStates.size === 0) {
      this.closeConnection();
    }
  }

  private addDescriptor(descriptor: TDescriptor): TDescriptor | undefined {
    const key = this.subscriptionKey(descriptor);
    const active = this.activeSubscriptions.get(key);
    if (active) {
      active.references++;
      return undefined;
    }

    this.activeSubscriptions.set(key, { descriptor, references: 1 });
    return descriptor;
  }

  private removeDescriptor(descriptor: TDescriptor): TDescriptor | undefined {
    const key = this.subscriptionKey(descriptor);
    const active = this.activeSubscriptions.get(key);
    if (!active) {
      return undefined;
    }

    active.references--;
    if (active.references > 0) {
      return undefined;
    }

    this.activeSubscriptions.delete(key);
    return active.descriptor;
  }

  private currentSubscribeMessages(): readonly TDescriptor[] {
    return Array.from(this.activeSubscriptions.values(), ({ descriptor }) => this.toSubscribeMessage(descriptor));
  }

  private handleTerminalConnection(connection: WebSocketConnection<TIncoming, TDescriptor>): void {
    if (this.connection !== connection) {
      return;
    }

    this.connection = undefined;
    this.connectionSubscription = undefined;
    this.activeSubscriptions.clear();
    this.subscriptionStates.clear();
  }

  private closeConnection(): void {
    const connection = this.connection;
    this.connection = undefined;
    this.connectionSubscription?.unsubscribe();
    this.connectionSubscription = undefined;
    connection?.close();
  }

  private subscriptionKey(descriptor: TDescriptor): string {
    return JSON.stringify(descriptor);
  }

  private toSubscribeMessage(descriptor: TDescriptor): TDescriptor {
    return descriptor;
  }

  private toUnsubscribeMessage(descriptor: TDescriptor): TDescriptor {
    return { ...descriptor, action: WEBSOCKET_ACTION_TYPE.UNSUBSCRIBE };
  }
}
